import React, { useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { useSalaAuth } from '../auth/SalaAuthContext';
import { SUBJECT_CATALOG } from '../studio/constants/subjectCatalog';

const SUBJECT_OPTIONS = SUBJECT_CATALOG.filter((subject) => subject.id !== 'general');

export function OnboardingPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo') || '/studio';
  const { user, profile, profileComplete, loading, saveProfile } = useSalaAuth();

  const [displayName, setDisplayName] = useState(
    () => profile?.displayName || user?.displayName || '',
  );
  const [subjectId, setSubjectId] = useState(() => profile?.subjectId || 'musica');
  const [schoolName, setSchoolName] = useState(() => profile?.schoolName || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const subjectTag = useMemo(
    () => SUBJECT_OPTIONS.find((subject) => subject.id === subjectId)?.tag || subjectId,
    [subjectId],
  );

  if (loading) {
    return (
      <div className="sala-login">
        <p className="text-sm text-[var(--micelio-slate)]">Cargando…</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  if (profileComplete) {
    return <Navigate to={returnTo} replace />;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (displayName.trim().length < 2) {
      setError('Escribe tu nombre.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await saveProfile({
        displayName: displayName.trim(),
        subjectId,
        subjectTag,
        schoolName: schoolName.trim() || undefined,
      });
      navigate(`/studio?seedCourses=1`, { replace: true });
    } catch {
      setError('No se pudo guardar el perfil. Intenta de nuevo.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sala-login">
      <div className="sala-login__panel">
        <p className="sala-login__eyebrow">Bienvenida/o a Sala</p>
        <h1 className="sala-login__title">Tu perfil docente</h1>
        <p className="sala-login__lead">
          Con tu nombre y asignatura sugeriremos cursos y rellenaremos los impresos.
        </p>

        <form className="sala-login__form" onSubmit={(event) => void handleSubmit(event)}>
          <label className="sala-login__label">
            Nombre completo
            <input
              className="sala-input-dark mt-1.5 w-full"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              placeholder="Ej: Ana Pérez"
              required
            />
          </label>

          <label className="sala-login__label">
            Asignatura principal
            <select
              className="sala-input-dark mt-1.5 w-full"
              value={subjectId}
              onChange={(event) => setSubjectId(event.target.value)}
            >
              {SUBJECT_OPTIONS.map((subject) => (
                <option key={subject.id} value={subject.id}>
                  {subject.icon} {subject.name}
                </option>
              ))}
            </select>
          </label>

          <label className="sala-login__label">
            Colegio <span className="font-normal normal-case">(opcional)</span>
            <input
              className="sala-input-dark mt-1.5 w-full"
              value={schoolName}
              onChange={(event) => setSchoolName(event.target.value)}
              placeholder="Ej: Colegio San Pedro"
            />
          </label>

          {error ? (
            <p className="sala-login__error" role="alert">
              {error}
            </p>
          ) : null}

          <button type="submit" className="sala-landing-cta w-full" disabled={busy}>
            {busy ? 'Guardando…' : 'Continuar a Studio'}
          </button>
        </form>
      </div>
    </div>
  );
}
